import { CustomActivity } from "./logging/CustomActivity";
import { IUser } from "./getCurrentUser";
import { shouldMockApis } from "./shouldMockApis";

interface IApiErrorInfo {
  ErrorMessage: string;
  ExceptionType: string;
  FullErrorMessage: string;
  ErrorCode: string | undefined;
  InternalErrorMessage: string | undefined;
}

export class ApiError extends Error {
  public readonly statusCode: number;
  public readonly body: string;
  public readonly errorInfo: IApiErrorInfo | undefined;
  constructor(statusCode: number, body: string, message: string) {
    super(message);
    this.statusCode = statusCode;
    this.body = body;
    this.name = "ApiError";
    if (body) {
      try {
        const json: IApiErrorInfo = JSON.parse(body);
        this.errorInfo = json;
      } catch (error) {
        console.error("Error parsing error body", error);
        return;
      }
    }
  }

  public static isApiError(error: Error): error is ApiError {
    // tslint:disable-next-line:no-any
    return !!(error as ApiError).statusCode;
  }

  public static buildString(apiError: ApiError): string {
    let strToPrint: string = "";
    if (apiError) {
      if (apiError.statusCode) {
        strToPrint += apiError.statusCode + "\n";
      }
      if (apiError.errorInfo) {
        if (apiError.errorInfo.ErrorCode) {
          strToPrint += apiError.errorInfo.ErrorCode + "\n";
        }
        if (apiError.errorInfo.ErrorMessage) {
          strToPrint += apiError.errorInfo.ErrorMessage + "\n";
        }
        if (apiError.errorInfo.ExceptionType) {
          strToPrint += apiError.errorInfo.ExceptionType + "\n";
        }
        if (apiError.errorInfo.FullErrorMessage) {
          strToPrint += apiError.errorInfo.FullErrorMessage + "\n";
        }
      }
    }
    return strToPrint;
  }
}

export type LabelRow = {
  FileName: string;
  CreatedDate: string;
  PointerToBlob: string;
  CreatedDateMs?: number;
};

export type AdminData = {
  users: Array<{
    UserId: string;
    Name: string;
    Timestamp: string;
    Bought: boolean;
    PaymentMethod?: string;
    PaymentId?: string;
    PaymentAmount?: number;
    PaymentCurrency?: string;
    PaymentDate?: string;
  }>;
  distributors: Array<{
    AvailableLicenses: number;
    LicensesBought: number;
    CloudUser?: { UserId: string };
    DistributorInfo: {
      CompanyName: string;
      DistribuidorAutorizado: boolean;
      RowKey: string;
    };
  }>;
  distributorPayments: Array<{
    PartitionKey: string;
    PaymentId: string;
    PaymentDate: string;
    PaymentMethod: string;
    PaymentAmount: number;
    PaymentCurrency: string;
    LicenseCount: number;
  }>;
  licenseAccepted: Array<{
    Distributor?: { CompanyName: string };
    LicenseInvitation: {
      LicenseId: string;
      LicenseInvitationEmail: string;
      AcceptedByUserId?: string;
      AcceptedDate?: string;
    };
    UserThatAccepted?: { Email: string; Name: string };
  }>;
};

export type BuySessionResponse =
  | {
      bought: true;
      paymentMethod?: string;
      paymentId?: string;
      paymentAmount?: number;
      paymentCurrency?: string;
      email?: string;
    }
  | {
      bought: false;
      sessionId: string;
      email?: string;
    };

export type IDistributorUserInfo = {
  CompanyName: string;
  Address: string;
  PhoneNumber: string;
  Street2: string;
  Number: string;
  City: string;
  State: string;
  Country: string;
  BusinessId: string;
  Zip: string;
  PartitionKey: string;
  RowKey: string;
  Timestamp: string;
  ETag: string;
  DistribuidorAutorizado: boolean;
};

export type IDistributorPayment = {
  PaymentMethod: string;
  PaymentId: string;
  LicenseCount: number;
  PaymentAmount: number;
  PaymentCurrency: string;
  PaymentDate: string;
  PartitionKey: string;
  RowKey: string;
  Timestamp: string;
  ETag: string;
};

export type IDistributorLicenseEmailMapping = {
  LicenseInvitationEmail: string;
  InsertionTime: string;
  LicenseId: string;
  AcceptedByUserId: string;
  AcceptedDate: string;
};

export type IDistributorInfo = {
  DistributorInfo: IDistributorUserInfo;
  DistributorLicenseMailMappings: IDistributorLicenseEmailMapping[];
  CloudUser: IUser;
  DistributorPayments: IDistributorPayment[];
  AvailableLicenses: number;
  LicensesBought: number;
};

export const queryKeys = {
  labels: ["labels"] as const,
  label: (fileId: string) => ["label", fileId] as const,
  admin: ["admin"] as const,
  buySession: ["buySession"] as const,
  eula: ["eula"] as const,
  distributorInfo: ["distributorInfo"] as const,
};

const MOCK_CLOUD_USER: IUser = {
  UserId: "117162129063344749731",
  Name: "jorgeaguirre1@gmail.com",
  Email: "jorgeaguirre1@gmail.com",
  EncryptedRefreshToken: "mock",
  Bought: false,
  EmailConfirmed: false,
  ConfirmEmailGuid: "9e7f98b5-f6ae-4b3c-8859-82e971a81e5f",
  AccountEmail: "jorgeaguirre1@gmail.com",
  AccountType: "Google",
  PaymentAmount: 0,
  IsAdmin: false,
  PartitionKey: "UserInfo",
  RowKey: "117162129063344749731",
  Timestamp: new Date("2019-03-11T00:52:57.1416795+00:00"),
  ETag: "W/datetime'2019-03-11T00%3A52%3A57.1416795Z'",
};

const MOCK_DISTRIBUTOR: IDistributorInfo = {
  DistributorInfo: {
    CompanyName: "Mock Distributor Co",
    Address: "3",
    PhoneNumber: "2",
    Street2: "4",
    Number: "5",
    City: "6",
    State: "7",
    Country: "8",
    BusinessId: "0",
    Zip: "9",
    PartitionKey: "DistributorsInfo",
    RowKey: "117162129063344749731",
    Timestamp: "2019-03-11T17:37:36.868404+00:00",
    ETag: "W/datetime'2019-03-11T17%3A37%3A36.868404Z'",
    DistribuidorAutorizado: true,
  },
  DistributorLicenseMailMappings: [
    {
      LicenseInvitationEmail: "customer@example.com",
      InsertionTime: "2024-03-01T10:00:00.000Z",
      LicenseId: "mock-license-1",
      AcceptedByUserId: "",
      AcceptedDate: "",
    },
  ],
  CloudUser: MOCK_CLOUD_USER,
  DistributorPayments: [
    {
      PaymentMethod: "Stripe",
      PaymentId: "Stripe|ch_1CgNY6JZNgXFrghdv5c4XBv5",
      LicenseCount: 2,
      PaymentAmount: 4859.01,
      PaymentCurrency: "mxn",
      PaymentDate: "2018-06-24T02:01:55.0926575Z",
      PartitionKey: "117162129063344749731",
      RowKey: "ch_1CgNY6JZNgXFrghdv5c4XBv5",
      Timestamp: "2018-06-24T02:01:55.1858025+00:00",
      ETag: "W/datetime'2018-06-24T02%3A01%3A55.1858025Z'",
    },
  ],
  AvailableLicenses: 1,
  LicensesBought: 2,
};

const MOCK_LABELS: LabelRow[] = [
  {
    FileName: "Demo label.din",
    CreatedDate: "2024-06-15T14:30:00.000Z",
    PointerToBlob: "mock-label-1",
    CreatedDateMs: Date.parse("2024-06-15T14:30:00.000Z"),
  },
  {
    FileName: "Shipping labels.din",
    CreatedDate: "2025-01-08T09:12:00.000Z",
    PointerToBlob: "mock-label-2",
    CreatedDateMs: Date.parse("2025-01-08T09:12:00.000Z"),
  },
];

const MOCK_ADMIN: AdminData = {
  users: [
    {
      UserId: "117162129063344749731",
      Name: "jorgeaguirre1@gmail.com",
      Timestamp: "2019-03-11T00:52:57.1416795+00:00",
      Bought: false,
    },
    {
      UserId: "mock-user-paid",
      Name: "paid.user@example.com",
      Timestamp: "2024-02-01T12:00:00.000Z",
      Bought: true,
      PaymentMethod: "Stripe",
      PaymentId: "ch_mock_paid",
      PaymentAmount: 180,
      PaymentCurrency: "mxn",
      PaymentDate: "2024-02-01T12:05:00.000Z",
    },
  ],
  distributors: [
    {
      AvailableLicenses: 2,
      LicensesBought: 2,
      CloudUser: { UserId: "117162129063344749731" },
      DistributorInfo: {
        CompanyName: "Mock Distributor Co",
        DistribuidorAutorizado: true,
        RowKey: "117162129063344749731",
      },
    },
    {
      AvailableLicenses: 0,
      LicensesBought: 0,
      CloudUser: { UserId: "mock-pending-distributor" },
      DistributorInfo: {
        CompanyName: "Pending Distributor SA",
        DistribuidorAutorizado: false,
        RowKey: "mock-pending-distributor",
      },
    },
  ],
  distributorPayments: [
    {
      PartitionKey: "117162129063344749731",
      PaymentId: "ch_1CgNY6JZNgXFrghdv5c4XBv5",
      PaymentDate: "2018-06-24T02:01:55.0926575Z",
      PaymentMethod: "Stripe",
      PaymentAmount: 4859.01,
      PaymentCurrency: "mxn",
      LicenseCount: 2,
    },
  ],
  licenseAccepted: [
    {
      Distributor: { CompanyName: "Mock Distributor Co" },
      LicenseInvitation: {
        LicenseId: "mock-license-1",
        LicenseInvitationEmail: "customer@example.com",
        AcceptedByUserId: "mock-user-paid",
        AcceptedDate: "2024-03-01T10:00:00.000Z",
      },
      UserThatAccepted: {
        Email: "paid.user@example.com",
        Name: "paid.user@example.com",
      },
    },
  ],
};

const MOCK_EULA = `DINLabel End User License Agreement (mock)

This is mock EULA text for local development when the API is not available.
By using this application you agree to the terms that would normally be
served from /privacypolicy/eulatext.
`;

export class RestApi {
  public static getApiBaseUrl(): string {
    // The base URL will be overriden, so make sure we override this one as well
    const url = new URL(window.location.href);
    return url.protocol + "//" + url.host;
  }

  public static async getErrorFromResponse(response: Response): Promise<Error> {
    const body = await response.text();
    const statusCode = response.status;
    return new ApiError(statusCode, body, "API request failed");
  }

  private static async ensureOk(response: Response, activity: CustomActivity): Promise<void> {
    if (response.ok) {
      activity.finishWithSuccess();
      return;
    }
    const error = await RestApi.getErrorFromResponse(response);
    activity.finishWithFailure("FailedRequest", error);
    throw error;
  }

  private static async parseJson<T>(response: Response, activity: CustomActivity): Promise<T> {
    await RestApi.ensureOk(response, activity);
    return response.json() as Promise<T>;
  }

  public static async getDistributorInfo(): Promise<IDistributorInfo | null> {
    if (shouldMockApis()) {
      console.info("[mock] getDistributorInfo");
      return structuredClone(MOCK_DISTRIBUTOR);
    }

    const activity = new CustomActivity("GetDistributorInfo");
    const response = await fetch(
      `${RestApi.getApiBaseUrl()}/distributor/getdistributorinfo`,
      { credentials: "same-origin" },
    );
    if (response.status === 401) {
      const error = new Error("unauthorized");
      activity.finishWithFailure("Unauthorized", error);
      throw error;
    }
    await RestApi.ensureOk(response, activity);
    const text = await response.text();
    if (!text || text === "null") {
      return null;
    }
    return JSON.parse(text) as IDistributorInfo;
  }

  public static async sendLicencia(mail: string): Promise<void> {
    if (shouldMockApis()) {
      console.info("[mock] sendLicencia", mail);
      return;
    }

    const getActivity = new CustomActivity("SendLicencia");
    const response = await fetch(
      `${RestApi.getApiBaseUrl()}/distributor/sendlicense`,
      {
        method: "POST",
        body: JSON.stringify({
          recipientEmail: mail,
          respondWithJson: "true",
        }),
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      },
    );
    await RestApi.ensureOk(response, getActivity);
  }

  public static async resendLicencia(
    rowKey: string,
    partitionKey: string,
    email: string,
  ): Promise<void> {
    if (shouldMockApis()) {
      console.info("[mock] resendLicencia", { rowKey, partitionKey, email });
      return;
    }

    const getActivity = new CustomActivity("ReSendLicencia");
    const response = await fetch(
      `${RestApi.getApiBaseUrl()}/distributor/resendlicense`,
      {
        method: "POST",
        body: JSON.stringify({
          rowKey: rowKey,
          partitionKey: partitionKey,
          email: email,
          respondWithJson: "true",
        }),
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      },
    );
    await RestApi.ensureOk(response, getActivity);
  }

  public static async loginAndReload() {
    if (shouldMockApis()) {
      console.info("[mock] loginAndReload");
      return;
    }

    const getActivity = new CustomActivity("LoginThroughAPI");
    const response = await fetch(`${RestApi.getApiBaseUrl()}/distributorportal`, {
      method: "POST",
      body: JSON.stringify({
        respondWithJson: "true",
      }),
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    });
    await RestApi.ensureOk(response, getActivity);
  }

  public static async getLabels(): Promise<LabelRow[]> {
    if (shouldMockApis()) {
      console.info("[mock] getLabels");
      return MOCK_LABELS;
    }

    const activity = new CustomActivity("GetLabels");
    const response = await fetch(`${RestApi.getApiBaseUrl()}/mylabels/getlabels`, {
      credentials: "same-origin",
    });
    return RestApi.parseJson<LabelRow[]>(response, activity);
  }

  public static async getLabel(fileId: string): Promise<LabelRow> {
    if (shouldMockApis()) {
      console.info("[mock] getLabel", fileId);
      const found = MOCK_LABELS.find((label) => label.PointerToBlob === fileId);
      return (
        found ?? {
          FileName: `Mock label (${fileId}).din`,
          CreatedDate: new Date().toISOString(),
          PointerToBlob: fileId,
        }
      );
    }

    const activity = new CustomActivity("GetLabel");
    const response = await fetch(
      `${RestApi.getApiBaseUrl()}/mylabels/getlabel?fileId=${encodeURIComponent(fileId)}`,
      { credentials: "same-origin" },
    );
    return RestApi.parseJson<LabelRow>(response, activity);
  }

  public static async getAdminInfo(): Promise<AdminData> {
    if (shouldMockApis()) {
      console.info("[mock] getAdminInfo");
      return structuredClone(MOCK_ADMIN);
    }

    const activity = new CustomActivity("GetAdminInfo");
    const response = await fetch(`${RestApi.getApiBaseUrl()}/admin/getadmininfo`, {
      credentials: "same-origin",
    });
    return RestApi.parseJson<AdminData>(response, activity);
  }

  public static async authorizeDistributor(userId: string): Promise<void> {
    if (shouldMockApis()) {
      console.info("[mock] authorizeDistributor", userId);
      const distributor = MOCK_ADMIN.distributors.find(
        (d) =>
          d.CloudUser?.UserId === userId || d.DistributorInfo.RowKey === userId,
      );
      if (distributor) {
        distributor.DistributorInfo.DistribuidorAutorizado = true;
      }
      return;
    }

    const activity = new CustomActivity("AuthorizeDistributor");
    const response = await fetch(
      `${RestApi.getApiBaseUrl()}/distributor/authorizedistributor`,
      {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ UserId: userId }),
      },
    );
    await RestApi.ensureOk(response, activity);
  }

  public static async createBuySession(): Promise<BuySessionResponse> {
    if (shouldMockApis()) {
      console.info("[mock] createBuySession");
      return {
        bought: false,
        sessionId: "mock-stripe-session-buy",
        email: "jorgeaguirre1@gmail.com",
      };
    }

    const activity = new CustomActivity("CreateBuySession");
    const response = await fetch(`${RestApi.getApiBaseUrl()}/buy/createsession`, {
      credentials: "same-origin",
    });
    if (response.status === 401) {
      const error = new Error("unauthorized");
      activity.finishWithFailure("Unauthorized", error);
      throw error;
    }
    return RestApi.parseJson<BuySessionResponse>(response, activity);
  }

  public static async createDistributorStripeSession(
    licenseCount: number,
  ): Promise<string> {
    if (shouldMockApis()) {
      console.info("[mock] createDistributorStripeSession", licenseCount);
      return `mock-stripe-session-distributor-${licenseCount}`;
    }

    const activity = new CustomActivity("CreateDistributorStripeSession");
    const response = await fetch(
      `${RestApi.getApiBaseUrl()}/api/stripe/createSessionForDistributor?licenseCount=${licenseCount}`,
      {
        method: "POST",
      },
    );
    return RestApi.parseJson<string>(response, activity);
  }

  public static async resendConfirmationEmail(): Promise<void> {
    if (shouldMockApis()) {
      console.info("[mock] resendConfirmationEmail");
      return;
    }

    const activity = new CustomActivity("ResendConfirmationEmail");
    const response = await fetch(
      `${RestApi.getApiBaseUrl()}/userinfo/resendConfirmationEmail`,
      { method: "POST", credentials: "same-origin" },
    );
    await RestApi.ensureOk(response, activity);
  }

  public static async getEulaText(): Promise<string> {
    if (shouldMockApis()) {
      console.info("[mock] getEulaText");
      return MOCK_EULA;
    }

    const activity = new CustomActivity("GetEulaText");
    const response = await fetch(`${RestApi.getApiBaseUrl()}/privacypolicy/eulatext`);
    if (!response.ok) {
      const error = await RestApi.getErrorFromResponse(response);
      activity.finishWithFailure("FailedRequest", error);
      throw error;
    }
    activity.finishWithSuccess();
    return response.text();
  }
}
